import React from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";

interface MultiDayCalendarSectionProps {
    selectedDates: Date[];
    setSelectedDates: (dates: Date[]) => void;
    viewDate: Date;
    setViewDate: (date: Date) => void;
    minDate: Date;
    maxDate: Date;
    disabledDays: number[];
    holidayDates: Date[];
    daysWithRecords: number[];
    onSelectAll: () => void;
    onClearSelection: () => void;
}

export const MultiDayCalendarSection: React.FC<MultiDayCalendarSectionProps> = ({
    selectedDates,
    setSelectedDates,
    viewDate,
    setViewDate,
    minDate,
    maxDate,
    disabledDays,
    holidayDates,
    daysWithRecords,
    onSelectAll,
    onClearSelection,
}) => {
    return (
        <div className="border-1 border-round-xl p-3 bg-white" style={{ borderColor: "#E2E8F0" }}>
            <div
                className="flex justify-content-between align-items-center mb-2"
                style={{ minHeight: "36px" }}
            >
                <div className="flex align-items-center gap-2">
                    <span className="text-sm font-semibold white-space-nowrap" style={{ color: "#1E293B" }}>
                        Seleciona a data
                    </span>
                    <span
                        className="text-xs font-bold px-2 py-1"
                        style={{
                            backgroundColor: selectedDates.length > 0 ? "#E6F0FB" : "#F1F5F9",
                            color: selectedDates.length > 0 ? "#0084D1" : "#64748B",
                            minWidth: "24px",
                            textAlign: "center",
                            borderRadius: "6px",
                        }}
                    >
                        {selectedDates.length}
                    </span>
                </div>

                <div className="flex align-items-center gap-1">
                    <Button
                        label="Todos"
                        text
                        size="small"
                        type="button"
                        onClick={onSelectAll}
                        className="p-1 text-xs font-semibold"
                        style={{ color: "#0084D1" }}
                    />
                    <Button
                        label="Limpar"
                        text
                        size="small"
                        type="button"
                        severity="danger"
                        onClick={onClearSelection}
                        disabled={selectedDates.length === 0}
                        className="p-1 text-xs font-semibold"
                        style={{
                            opacity: selectedDates.length > 0 ? 1 : 0,
                            pointerEvents: selectedDates.length > 0 ? "auto" : "none",
                            transition: "opacity 0.2s ease",
                        }}
                    />
                </div>
            </div>

            <div className="w-full">
                <Calendar
                    value={selectedDates}
                    onChange={(e) => {
                        const newValue = (e.value as Date[]) ?? [];
                        setSelectedDates(newValue);
                    }}
                    selectionMode="multiple"
                    inline
                    viewDate={viewDate}
                    onViewDateChange={(e) => {
                        if (e.value) setViewDate(e.value);
                    }}
                    disabledDays={disabledDays}
                    disabledDates={holidayDates}
                    minDate={minDate}
                    maxDate={maxDate}
                    showOtherMonths={false}
                    className="w-full"
                    dateTemplate={(dateObj) => {
                        const dayNum = dateObj.day;
                        const hasRecord = daysWithRecords.includes(dayNum);
                        return (
                            <div className="flex flex-column align-items-center">
                                <span>{dayNum}</span>
                                {hasRecord && (
                                    <div
                                        style={{
                                            width: "4px",
                                            height: "4px",
                                            borderRadius: "50%",
                                            backgroundColor: "#0084D1",
                                            marginTop: "1px",
                                        }}
                                    />
                                )}
                            </div>
                        );
                    }}
                    pt={{
                        root: {
                            className: "w-full border-none p-0",
                            style: { border: "none", background: "transparent" },
                        },
                        header: {
                            className: "border-none pb-2 pt-0",
                            style: {
                                border: "none",
                                background: "transparent",
                                justifyContent: "center",
                                padding: "0 0 0.5rem 0",
                            },
                        },
                        title: {
                            className: "text-base font-semibold",
                            style: { margin: 0, textTransform: "capitalize" },
                        },
                        monthTitle: {
                            style: { pointerEvents: "none", cursor: "default", fontWeight: 600, color: "#1E293B" },
                        },
                        yearTitle: {
                            style: { pointerEvents: "none", cursor: "default", fontWeight: 600, color: "#1E293B", marginLeft: "0.5rem" },
                        },
                        table: {
                            className: "w-full",
                            style: { tableLayout: "fixed", margin: 0 },
                        },
                        tableHeaderRow: {
                            style: { borderBottom: "1px solid #F1F5F9" },
                        },
                        tableHeaderCell: {
                            className: "text-center text-xs font-semibold py-2",
                            style: { color: "#64748B" },
                        },
                        day: {
                            className: "text-center p-0",
                            style: { padding: "3px 0" },
                        },
                        dayLabel: {
                            className: "w-2rem h-2rem flex align-items-center justify-content-center text-sm mx-auto",
                            style: { borderRadius: "8px" },
                        },
                        previousButton: { style: { display: "none" } },
                        nextButton: { style: { display: "none" } },
                    }}
                />
            </div>
        </div>
    );
};
